import React, { ComponentType, ReactNode } from 'react';

import { ErrorBoundary } from './ErrorBoundary';

interface WithErrorBoundaryProps {
  fallback?: ReactNode;
  children?: ReactNode;
}

/**
 * Higher-Order Component that wraps a component with ErrorBoundary
 * Usage: export default withErrorBoundary(MyComponent);
 */
export function withErrorBoundary<P extends object>(
  Component: ComponentType<P>,
  fallback?: ReactNode,
): ComponentType<P & WithErrorBoundaryProps> {
  const WithErrorBoundaryComponent = (props: P & WithErrorBoundaryProps) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WithErrorBoundaryComponent;
}

/**
 * Component wrapper that adds ErrorBoundary around children
 * Usage: <WithErrorBoundary><MyComponent /></WithErrorBoundary>
 */
export const WithErrorBoundary: React.FC<WithErrorBoundaryProps> = ({ children, fallback }) => (
  <ErrorBoundary fallback={fallback}>
    {children}
  </ErrorBoundary>
);
